import { convertDDToDMS, Feature, lonLatConversion } from "@igo2/geo";
import GeoJSON from 'ol/format/GeoJSON';
import type { default as OlFeature } from 'ol/Feature';
import { Geometry } from "ol/geom";
import { getDistance } from 'ol/sphere';

export class RapportXYDto {
    mtmFuseau: string;
    mtmNord: number;
    mtmEst: number;
    utmFuseau: string;
    utmNord: number;
    utmEst: number;
    longitude: string;
    latitude: string;
    longitudeDMS: string;
    latitudeDMS: string;
    conventionDeGestionTerritorialeDelegataire?: string = "Aucun";
    conventionDeGestionTerritorialeSubdelegataire?: string = "Aucun";
    villegiatureAbriSommaireSableGravierDelegataire?: string = "Aucun";
    villegiatureAbriSommaireSableGravierSubdelegataire?: string = "Aucun";
    zoneDeRecreationPrincipaleParcRegionalDelegataire?: string = "Aucun";
    zoneDeRecreationPrincipaleParcRegionalSubdelegataire?: string = "Aucun";
    parcRegional?: string = "Aucun";
    zoneAccessibleAuxPremiersRequerants: boolean;
    domanialiteType?: string;
    domanialiteAutorite?: string;
    bdtq?: string;
    bdta?: string;
    municipalite?: string;
    municipaliteCode?: string;
    municipaliteCodeDesignation?: string;
    regionAdministrative?: string;
    regionAdministrativeCode?: string;
    typeDeTerritoire?: string;
    mrc?: string;
    mrcCode?: string;
    planDEau?: string;
    planDEauDistance?: string;
    numeroLotRenove?: string;
    niveau1?: string;
    niveau1Type?: string;
    niveau2?: string;
    niveau2Type?: string;
    niveau3?: string;
    niveau3Type?: string;
    zecReserveFauniquePourvoirie?: string;
    airesProtegeesDuRegistreCode?: string = "Aucune";
    uniteDAmenagementCode?: string;
    trDossierGDFTerritoire?: string;
    trDossierGDFRegion?: string;
    trDossierGDFNumeroTerritoire?: string;
    pole?: string;
    poleCode?: string;
    classeDEloignement?: string;
    classeDEloignementCode?: string;

    constructor(longitude: number, latitude: number) {
        this.longitude = longitude.toFixed(6);
        this.latitude = latitude.toFixed(6);

        const mtmUtmConversion = lonLatConversion([longitude, latitude], []);
        const mtm = mtmUtmConversion.find(f => f.alias.toLowerCase() === 'mtm');
        this.mtmEst = Math.round(mtm.coord[0]);
        this.mtmNord = Math.round(mtm.coord[1]);
        this.mtmFuseau = mtm.igo2CoordFormat.split(" ")[0].replace(/MTM-/gi, '');
        const utm = mtmUtmConversion.find(f => f.alias.toLowerCase() === 'utm');
        this.utmEst = Math.round(utm.coord[0]);
        this.utmNord = Math.round(utm.coord[1]);
        this.utmFuseau = utm.igo2CoordFormat.split(" ")[0].replace(/UTM-/gi, '');

        const dms = convertDDToDMS([longitude, latitude]);
        this.longitudeDMS = dms[0].replace(/\.000"/, '"');
        this.latitudeDMS = dms[1].replace(/\.000"/, '"');
    }

    buildDataFor(section: string, features: Feature[], geom: Geometry) {
        if (features.length) {
            const feature = features[0];
            switch (section) {
                case 'TerritoireConventionGestionTerrritorialeSurface': this.buildTerritoireConventionGestionTerrritoriale(feature); break;
                case 'VillegiatureAbriSommaireSableGravierSurface': this.buildVillegiatureAbriSommaireSableGravier(feature); break;
                case 'ZoneRecreationPrincipaleParcRegionalSurface': this.buildZoneRecreationPrincipaleParcRegional(feature); break;
                case 'PremiersRequerantsSurface': this.buildPremiersRequerants(feature); break;
                case 'PoleAttractionSurface': this.buildPole(feature); break;
                case 'ClasseEloignementSurface': this.buildClasseEloignement(feature); break;
                case 'Index_cartographique_20K': this.buildBDTQ(feature); break;
                case 'Index_cartographique_250K': this.buildBDTA(feature); break;
                case 'TerritoireGDF': this.buildGDF(feature); break;
                case 'GRHQToponyme': this.buildPlanDEau(features, geom); break;
                default: console.error(`Aucune méthode pour construire la section '${section}'`);
            }
        }
    }

    buildDataForRestRequest(section: string, feature: Feature) {
        switch (section) {
            case 'municipalite': this.buildDataForMunicipality(feature); break;
            case 'ua': this.buildDataForUniteAmenagement(feature); break;
            case 'zec':
            case 'zec-reserve-faunique':
            case 'zec-pourvoirie': this.buildDataForZec(feature); break;
            case 'aires-protegees': this.buildDataForAiresProtegees(feature); break;
            default: console.error(`Aucune méthode pour construire la section '${section}'`);
        }
    }

    buildDataForWmsRequest(section: string, features: Feature[]) {
        if (features.length) {
            switch (section) {
                case 'WMS_FONCIER:LotCadastre':
                    this.numeroLotRenove = features[0].properties.NO_LOT;
                    break;
                case 'WMS_FONCIER:ArpentageCompilePolygone':
                    this.niveau1 = features[0].properties.DESCRIPTION_NIVEAU_1;
                    this.niveau1Type = features[0].properties.TYPE_NIVEAU_1;
                    this.niveau2 = features[0].properties.DESCRIPTION_NIVEAU_2;
                    this.niveau2Type = features[0].properties.TYPE_NIVEAU_2;
                    this.niveau3 = features[0].properties.DESCRIPTION_NIVEAU_3;
                    this.niveau3Type = features[0].properties.TYPE_NIVEAU_3;
                    break;
                case 'WMS_FONCIER:Domanialite':
                    this.domanialiteType = features[0].properties.DESCRIPTION;
                    this.domanialiteAutorite = features[0].properties.NOM_ORGANISME;
                    break;
            }
        }
    }

    buildTerritoireConventionGestionTerrritoriale(feature: Feature) {
        this.conventionDeGestionTerritorialeDelegataire = feature.properties.DELEGATAIRE;
        this.conventionDeGestionTerritorialeSubdelegataire = feature.properties.SUBDELEGATAIRE;
    }

    buildVillegiatureAbriSommaireSableGravier(feature: Feature) {
        this.villegiatureAbriSommaireSableGravierDelegataire = feature.properties.DELEGATAIRE || "Aucun";
        this.villegiatureAbriSommaireSableGravierSubdelegataire = feature.properties.SUBDELEGATAIRE || "Aucun";
    }

    buildZoneRecreationPrincipaleParcRegional(feature: Feature) {
        this.zoneDeRecreationPrincipaleParcRegionalDelegataire = feature.properties.DELEGATAIRE;
        this.zoneDeRecreationPrincipaleParcRegionalSubdelegataire = feature.properties.SUBDELEGATAIRE;
        this.parcRegional = feature.properties.NOM_ENTITE;
    }

    buildPremiersRequerants(_feature: Feature) {
        this.zoneAccessibleAuxPremiersRequerants = true;
    }

    buildPole(feature: Feature) {
        this.pole = feature.properties.NOM_POLE;
        this.poleCode = feature.properties.NUMERO_POLE;
    }

    buildClasseEloignement(feature: Feature) {
        this.classeDEloignement = feature.properties.CLASSE_ELOIGNEMENT;
        this.classeDEloignementCode = feature.properties.NUMERO_CLASSE_ELOIGNEMENT;
    }

    buildBDTQ(feature: Feature) {
        this.bdtq = feature.properties.NUMERO_FEUILLET_ABREGE1;
    }

    buildBDTA(feature: Feature) {
        this.bdta = feature.properties.NUMERO_FEUILLET_COMPLET;
    }

    buildGDF(feature: Feature) {
        this.trDossierGDFTerritoire = feature.properties.NOM_TR;
        this.trDossierGDFRegion = feature.properties.NOM_REGION;
        this.trDossierGDFNumeroTerritoire = feature.properties.NUMERO_TR;
    }

    buildDataForMunicipality(feature: Feature) {
        let featureArcGis = feature as any;
        this.municipalite = featureArcGis?.attributes?.MUS_NM_MUN || "Aucune";
        this.municipaliteCode = featureArcGis?.attributes?.MUS_CO_GEO;
        this.municipaliteCodeDesignation = featureArcGis?.attributes?.MUS_CO_DES;
        this.regionAdministrative = featureArcGis?.attributes?.MUS_NM_REG || "Aucune";
        this.regionAdministrativeCode = featureArcGis?.attributes?.MUS_CO_REG;
        this.typeDeTerritoire = featureArcGis?.attributes?.MUS_DE_IND || "Aucun";
        this.mrc = featureArcGis?.attributes?.MUS_NM_MRC || "Aucune";
        this.mrcCode = featureArcGis?.attributes?.MUS_CO_MRC;
    }

    buildPlanDEau(features: Feature[], point: Geometry) {
        const geoJson = new GeoJSON();
        let minDistance: number = undefined;
        let nearestFeature: Feature;
        features.forEach(feature => {
            const featureGeom = (geoJson.readFeature(feature) as OlFeature<Geometry>).getGeometry();
            const pointAsAny = point as any;
            const closestPoint = featureGeom.getClosestPoint([pointAsAny.flatCoordinates[0], pointAsAny.flatCoordinates[1]]);
            const distance = getDistance(
                [pointAsAny.flatCoordinates[0], pointAsAny.flatCoordinates[1]],
                [closestPoint[0], closestPoint[1]]);
            minDistance = minDistance ?? distance;
            if (distance <= minDistance) {
                minDistance = distance;
                nearestFeature = feature;
            }
        });
        this.planDEau = nearestFeature.properties.TOPONYME ?? "Absence de toponyme";
        this.planDEauDistance = (minDistance / 1000).toFixed(2);
    }

    buildDataForUniteAmenagement(feature: Feature) {
        let featureArcGis = feature as any;
        this.uniteDAmenagementCode = featureArcGis ? featureArcGis.attributes.NO_UG : "Aucune";
    }

    buildDataForZec(feature: Feature) {
        let featureArcGis = feature as any;
        if (featureArcGis) {
            this.zecReserveFauniquePourvoirie = featureArcGis.attributes.NOM_OFFICIEL;
        }
    }

    buildDataForAiresProtegees(feature: Feature) {
        let featureArcGis = feature as any;
        if (featureArcGis) {
            this.airesProtegeesDuRegistreCode = featureArcGis.attributes.TOPONYME;
        }
    }
}
